import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { REGEX_EMAIL, REGEX_PASSWORD } from '../services/models/user';
import Service, { isServiceReady } from '../services/Service';
import UserService from '../services/UserService';
import { Field, Locale } from '../types/common';
import { Button, Input } from '@follow/ui';



const Signup = () => {
  const [email, setEmail] = useState<Field>({ value: '' });
  const [password, setPassword] = useState<Field>({ value: '' });
  const [confirmPassword, setConfirmPassword] = useState<Field>({ value: '' });
  const [submitted, setSubmitted] = useState(false);
  const [firstName, setFirstName] = useState<Field>({ value: '' });
  const [lastName, setLastName] = useState<Field>({ value: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const navigate = useNavigate();

  const registerMutation = useMutation({
    mutationFn: (data: Parameters<typeof UserService.register>[0]) =>
      UserService.register(data),
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (error: any) => {
      const messages: Record<string, string> = {
        EMAIL_ALREADY_IN_USE: 'Un compte existe déjà avec cet email.',
      };
      toast.error(messages[error?.code] || error?.message || 'Erreur lors de la création du compte');
    },
  });

  useEffect(() => {
    if (!isServiceReady) {
      Service.init(() => {
        localStorage.removeItem('token');
        navigate('/login');
      });
    }
  }, [navigate]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      verifyEmail();
      verifyPassword();
      verifyConfirmPassword();
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    email.value,
    password.value,
    confirmPassword.value,
  ]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail({ value: event.target.value });
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword({ value: event.target.value });
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword({ value: event.target.value });
  };

  const verifyEmail = () => {
    setEmail((prev) => {
      if (prev.value && prev.value.length > 0) {
        return { value: prev.value, error: !REGEX_EMAIL.test(prev.value) };
      }
      return { value: prev.value };
    });
  };

  const verifyPassword = () => {
    setPassword((prev) => {
      if (prev.value && prev.value.length > 0) {
        const passwordRegex = REGEX_PASSWORD;
        return { value: prev.value, error: !passwordRegex.test(prev.value) };
      }
      return { value: prev.value };
    });
  };

  const verifyConfirmPassword = () => {
    setConfirmPassword((prev) => {
      if (prev.value && prev.value.length > 0) {
        return { value: prev.value, error: prev.value !== password.value };
      }
      return { value: prev.value };
    });
  };

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName({ value: event.target.value });
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName({ value: event.target.value });
  };

  const handleTermsAcceptedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(event.target.checked);
  };

  const checkData = () => {
    return (
      (email.value?.length ?? 0) > 0 && !email.error
      && (password.value?.length ?? 0) > 0 && !password.error
      && (confirmPassword.value?.length ?? 0) > 0 && !confirmPassword.error
      && (firstName.value?.length ?? 0) > 0 && !firstName.error
      && (lastName.value?.length ?? 0) > 0 && !lastName.error
      && termsAccepted
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!registerMutation.isPending && checkData()) {
      registerMutation.mutate({
        email: email.value!,
        password: password.value!,
        confirmPassword: confirmPassword.value!,
        firstName: firstName.value!.trim(),
        lastName: lastName.value!.trim(),
        termsAccepted,
        lang: Locale.FR,
      });
    }
  };

  return (
    <div className="flex min-h-screen bg-off-white overscroll-none overflow-hidden">
      {/* LEFT PANEL */}
      <div className="w-full lg:w-[35%] bg-secondary-900 flex flex-col">
        <div className="p-8">
          <a href={process.env.REACT_APP_LANDING_URL || '/'}>
            <img src="/logo_full_light.svg" alt="Follow" className="h-7 w-auto" />
          </a>
        </div>

        <div className="flex-1 overflow-y-auto px-5 lg:px-16 pb-8">
          {submitted ? (
            <div className="w-full max-w-[448px] mx-auto mt-16">
              <h1 className="text-white/80 typo-h1">Vérifiez votre email</h1>
              <p className="text-white/70 typo-body-lg mt-2">
                Un email de vérification a été envoyé à <strong className="text-white/90">{email.value}</strong>. Cliquez sur le lien pour activer votre compte.
              </p>
              <p className="text-white/40 typo-small mt-4">
                Vous n'avez pas reçu l'email ? Vérifiez vos spams ou{' '}
                <Link to="/login" className="text-white/70 hover:underline">
                  retournez à la connexion
                </Link>.
              </p>
            </div>
          ) : (
            <form className="w-full max-w-[448px] mx-auto" onSubmit={handleSubmit}>
            <h1 className="text-white/80 typo-h1">Créer un compte</h1>
            <p className="text-white/70 typo-body-lg mt-2">Inscrivez-vous pour commencer</p>

            {/* Email */}
            <div className="mt-8">
              <label className="block text-white/70 typo-small mb-2" htmlFor="email">
                Email
              </label>
              <Input
                tone="light"
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email.value}
                onChange={handleEmailChange}
              />
              {email.error && (
                <p className="text-red-400 typo-small mt-1">L'email n'est pas valide.</p>
              )}
            </div>

            {/* Password + Confirm Password */}
            <div className="mt-4 flex gap-4">
              <div className="flex-1">
                <label className="block text-white/70 typo-small mb-2" htmlFor="password">
                  Mot de passe
                </label>
                <Input
                  tone="light"
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password.value}
                  onChange={handlePasswordChange}
                />
                {password.error && (
                  <p className="text-red-400 typo-small mt-1">Mot de passe invalide.</p>
                )}
              </div>
              <div className="flex-1">
                <label className="block text-white/70 typo-small mb-2" htmlFor="confirmPassword">
                  Confirmer
                </label>
                <Input
                  tone="light"
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword.value}
                  onChange={handleConfirmPasswordChange}
                />
                {confirmPassword.error && (
                  <p className="text-red-400 typo-small mt-1">Ne correspond pas.</p>
                )}
              </div>
            </div>
            <p className="mt-1 text-white/40 typo-small">
              8 caractères minimum, 1 majuscule, 1 minuscule, 1 chiffre
            </p>

            {/* First Name + Last Name */}
            <div className="mt-4 flex gap-4">
              <div className="flex-1">
                <label className="block text-white/70 typo-small mb-2" htmlFor="firstName">
                  Prénom
                </label>
                <Input
                  tone="light"
                  id="firstName"
                  value={firstName.value}
                  onChange={handleFirstNameChange}
                />
              </div>
              <div className="flex-1">
                <label className="block text-white/70 typo-small mb-2" htmlFor="lastName">
                  Nom
                </label>
                <Input
                  tone="light"
                  id="lastName"
                  value={lastName.value}
                  onChange={handleLastNameChange}
                />
              </div>
            </div>

            {/* Terms */}
            <div className="mt-5 flex items-center gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={handleTermsAcceptedChange}
                className="rounded border-white/30"
              />
              <label className="text-white/70 typo-small" htmlFor="terms">
                J'accepte les{' '}
                <a href={`${process.env.REACT_APP_LANDING_URL}/terms-and-conditions`} target="_blank" rel="noopener noreferrer" className="text-white/90 underline hover:text-white">
                  conditions d'utilisation
                </a>
              </label>
            </div>

            {/* Submit */}
            <div className="mt-6">
              <Button
                type="submit"
                variant="primary"
                tone="light"
                size="lg"
                className="w-full"
                disabled={!checkData()}
                loading={registerMutation.isPending}
              >
                S'inscrire
              </Button>
            </div>

            {/* Login link */}
            <p className="mt-6 text-center text-white/70 typo-small">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-white/90 font-medium hover:underline">
                Se connecter
              </Link>
            </p>
          </form>
          )}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="hidden lg:flex lg:w-[65%] relative bg-secondary-900 border-l border-secondary-800 overflow-hidden lg:flex-col items-center justify-center">
        <img
          src="/signin-background.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/15" />

        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full gap-12">
          <h2 className="text-white typo-display text-center leading-tight">
            <span className="block">{'Transformez vos stations en'}</span>
            <span className="block text-primary-500">{' produits connectés'}</span>
          </h2>
          <p className="text-white typo-h3 text-center">
            Une solution intégrée pour piloter vos stations à distance.
          </p>

          <div className="flex justify-end -mt-32">
            <img
              src="/signin-lock.png"
              alt="Station Follow"
              className="w-[580px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
